/**
 * @author Owais Liaquat Ali
 * @datetime September, 18th 2023
 * @description: This component is main component for adding new user group flow
 */
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { Building } from 'src/app/building-managment/building-management-interfaces';
import { BuildingManagementService } from 'src/app/building-managment/building-management.service';
import { ConfirmDialogComponent } from 'src/app/shared-layout/Components/confirm-dialog/confirm-dialog.component';
import { ConfirmRemoveDialogComponent } from 'src/app/shared-layout/Components/confirm-remove-dialog/confirm-remove-dialog.component';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { CommonService } from 'src/app/shared/utils/common.service';
import { HelperService } from 'src/app/shared/utils/helper.service';
import {
  addFlagIfMatched,
  convertDateTimeFormat,
  convertParseDateTime,
} from 'src/app/shared/utils/utility.service';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import {
  IUserEntity,
  UserEntities,
} from '../../add-user/add-user-management.interface';
import { MultiUserSelectionListComponent } from '../../multi-user-share-access/multi-user-selection-list/multi-user-selection-list.component';
import {
  IAccessPointEntity,
  IUserGroupDetails,
  IUserGroupUpdateDetails,
} from '../../user-management-interfaces';
import { UserManagementService } from '../../user-management.service';
import { IAppState } from 'src/app/shared/state-management/state-interfaces';
import { NgRedux } from '@angular-redux/store';
import { UserCards } from 'src/app/shared-layout/Components/activity/user-card.interface';
import { AccessLevelRequest } from 'src/app/shared/constants/request-mapping';
import { TranslateService } from '@ngx-translate/core';
@Component({
  selector: 'app-user-profile-group',
  templateUrl: './user-profile-group.component.html',
  styleUrls: ['./user-profile-group.component.scss'],
})
export class UserProfileGroupComponent implements OnInit, OnDestroy {
  formGroup!: FormGroup;
  subscription: Subscription | undefined;
  readonly CONSTANTS = CONSTANTS;
  readonly LANG_CONST = LANG_CONST;
  stepper = 1;
  errorMessage: string | undefined;
  sharedData!: any;
  buildingList!: Building;
  isEditMode!: boolean;
  orignalFormData: string | undefined;
  accessPointEntityOrignal: string[] = [];
  users: any;
  existingUsers: any;
  selectedUsers: any;
  dashboardList!: UserCards;

  userGroupList: IUserGroupDetails[] = [];
  @Input() userStepper?: number;
  @Output() userStepperChange = new EventEmitter<number>();

  @Input() userGroup?: IUserGroupDetails[] | undefined;
  @Output() userGroupChange = new EventEmitter<any>();

  @Input() selectedGroup?: IUserGroupDetails[] | undefined;
  @Output() selectedGroupChange = new EventEmitter<any>();
  @Output() submitUserGroupToParent = new EventEmitter<any>();

  @Input() skipStepUserType: boolean = false;
  @Input() skipStepBuilding: boolean = false;

  @Input() preSelectedGroups?: IUserGroupDetails[] | undefined;
  @Input() userName?: string | undefined;

  @Input() siteTimeZone?: string | undefined;
  @Input() profileDetails: any;
  variables: any;

  isUserProfileFlow: boolean = false;
  @ViewChild(MultiUserSelectionListComponent) selectionListComponent:
    | MultiUserSelectionListComponent
    | undefined;

  /**
   * @description constructor injects all necesary services
   * @param { UserProfileGroupComponent } dialogRef
   */
  constructor(
    public dialogRef: MatDialogRef<UserProfileGroupComponent>,
    private formBuilder: FormBuilder,
    private userManagementService: UserManagementService,
    private notificationService: NotificationService,
    private buildingManagementService: BuildingManagementService,
    private commonService: CommonService,
    public sharedDataService: SharedDataService,
    private helperService: HelperService,
    private ngRedux: NgRedux<IAppState>,
    @Inject(MAT_DIALOG_DATA) private modalData: any,
    private translate: TranslateService
  ) {}

  /**
   *  @description call when initialize time do necessary feature add
   */
  ngOnInit(): void {
    this.subscription = this.ngRedux
      .select('dashboardActivities')
      .subscribe((dashboardActivity: any) => {
        this.dashboardList = dashboardActivity;
      });
    this.createControls();
    this.sharedDataService.currentData.subscribe((sharedData) => {
      this.sharedData = sharedData;
    });
    this.getBuildingList();
    this.getUserGroupList();
    this.getUsersBySiteId(this.sharedData?.selectedSite?.siteId);
  }

  /**
   * @description Create all child component into parents
   */
  createControls(): void {
    this.formGroup = this.formBuilder.group({
      addUserGroup: this.formBuilder.group({
        id: new FormControl(null),
        name: new FormControl(
          null,
          MFValidators.compose([
            MFValidators.required,
            MFValidators.minLength(3),
            MFValidators.maxLength(50),
            MFValidators.whitespace,
            MFValidators.entityName,
          ])
        ),
        description: new FormControl(
          null,
          MFValidators.compose([
            MFValidators.required,
            MFValidators.minLength(5),
            MFValidators.maxLength(255),
            MFValidators.whitespace,
            MFValidators.alphaNumSpecialChar,
          ])
        ),
        isDefault: new FormControl(null),
      }),
      deviceGroup: this.formBuilder.group({
        group: [Array()],
      }),
      buildingList: this.formBuilder.group({
        entities: [null, MFValidators.compose([MFValidators.required])],
      }),
      accessTime: this.formBuilder.group(
        {
          startTime: [''],
          expireTime: [''],
          repeat: [false],
        },
        {
          validator: [
            MFValidators.startTimeEndTime('startTime', 'expireTime'),
            MFValidators.siteZoneValidator('expireTime', this.siteTimeZone!),
          ],
        }
      ),
    });

    // in case form state create mode
    if (!this.modalData.isEditable)
      this.orignalFormData = JSON.stringify(this.formGroup.value);
  }

  /**
   * @description get all building from store
   * @returns void
   */
  getBuildingList(): void {
    this.buildingManagementService
      .getAllBuildingsBySiteId(this.sharedData?.selectedSite?.siteId)
      .subscribe({
        next: (buildings: Building) => {
          this.buildingList = buildings;
        },
        error: (err: any) => {
          if (window.__dev__) console.log('getBuildingList', err);
        },
      });
  }

  /**
   * @description get all groups
   * @returns void
   */
  getUserGroupList(): void {
    this.userManagementService
      .getUserGroupsBySite(this.sharedData?.selectedSite?.siteId)
      .subscribe({
        next: (userGroupList: IUserGroupDetails[]) => {
          this.userGroupList = userGroupList?.map(
            (userGroup: IUserGroupDetails) => {
              let selecteIndex = undefined;
              if (
                this.selectedGroup &&
                typeof this.selectedGroup === 'object' &&
                this.selectedGroup?.length > 0
              )
                selecteIndex = this.selectedGroup?.findIndex(
                  (selectedGroup: IUserGroupDetails) =>
                    selectedGroup?.userGroupId === userGroup.userGroupId
                );
              if (!(selecteIndex == -1 || selecteIndex == undefined)) {
                userGroup.active = true;
              } else {
                userGroup.active = false;
              }
              return userGroup;
            }
          );
          userGroupList = addFlagIfMatched(this.dashboardList, userGroupList);

          //removing pre selected groups for add new group feature
          if (this.preSelectedGroups && this.userGroupList) {
            this.isUserProfileFlow = true;
            this.userGroupList = this.userGroupList?.filter((group: any) => {
              for (let preSelectedGroup of this.preSelectedGroups || []) {
                if (group?.userGroupId === preSelectedGroup?.userGroupId) {
                  return false;
                }
              }
              return true;
            });
          }

          this.formGroup?.controls?.deviceGroup.patchValue({
            group: this.userGroupList?.filter(
              (userGroup: IUserGroupDetails) => userGroup?.active === true
            ),
          });
        },
        error: (err: any) => {
          if (window.__dev__) console.log('getBuildingList', err);
        },
      });
  }

  patchFormValues(uGroupDetails: IUserGroupDetails | undefined): void {
    if (uGroupDetails) {
      this.getUserGroupInfo(uGroupDetails?.userGroupId || '');
      this.formGroup.controls.addUserGroup.patchValue({
        id: uGroupDetails?.userGroupId,
        name: uGroupDetails?.userGroupName,
        description: uGroupDetails?.userGroupDescription,
        isDefault: uGroupDetails?.isDefault,
      });
      this.isEditMode = true;
    } else {
      this.formGroup?.controls?.addUserGroup?.reset();
      this.formGroup?.controls?.buildingList?.reset();
      this.isEditMode = false;
      this.accessPointEntityOrignal = [];
    }
  }

  getUserGroupInfo(userGroupId: string) {
    this.accessPointEntityOrignal = [];
    this.buildingList?.forEach((building: any, index: any) => {
      if (this.buildingList) this.buildingList[index].buildingCount = 0;
    });
    this.userManagementService.getUserGroupInfo(userGroupId).subscribe({
      next: (userGroupInfo: any) => {
        let entities: any = {};
        if (userGroupInfo?.accessPoints) {
          try {
            entities = JSON.parse(userGroupInfo?.accessPoints);
          } catch (err) {
            if (window.__dev__) console.log('getUserGroupInfo', err);
            entities = {};
          }
        }
        let accessPointEntities: any = [];
        for (let entity of Object.keys(entities)) {
          let accessPoints = entities[entity];
          this.buildingList?.forEach((building: any, index: any) => {
            if (
              this.buildingList &&
              building?.buildingId === entity &&
              this.isEditMode
            )
              this.buildingList[index].buildingCount = accessPoints?.length;
          });
          this.accessPointEntityOrignal =
            this.accessPointEntityOrignal.concat(accessPoints);
          accessPoints.forEach((accessPointRecordId: string) => {
            accessPointEntities.push({
              accessPointId: accessPointRecordId,
              buildingId: entity,
            });
          });
        }
        this.formGroup.controls.buildingList.patchValue({
          entities: accessPointEntities ? accessPointEntities : null,
        });
      },
      error: (err: any) => {
        if (window.__dev__) console.log('getBuildingList', err);
      },
    });
  }

  /**
   * @description form submit get all fromgroup reference here to transform data
   * as per server object requirement
   * @returns void
   */
  submitToParent(): void {
    let userGroupDetails: IUserGroupDetails = {};
    if (this.isEditMode)
      userGroupDetails.userGroupId = this.formGroup.value?.addUserGroup?.id;
    userGroupDetails.userGroupName =
      this.formGroup?.getRawValue()?.addUserGroup?.name;
    userGroupDetails.userGroupDescription =
      this.formGroup?.getRawValue()?.addUserGroup?.description;
    userGroupDetails.siteId = this.sharedData?.selectedSite?.siteId;

    const userEntities: UserEntities | any =
      this.formGroup.value?.buildingList?.entities;

    userGroupDetails.accessPoints = userEntities?.map((entity: IUserEntity) => {
      return entity?.accessPointId;
    });

    //if selectedUsers exists, add it to the payload
    if (this.selectedUsers) userGroupDetails.selectedUsers = this.selectedUsers;

    if (this.isEditMode) {
      let addAccessPoints: IAccessPointEntity[] = [];
      let deleteAccessPoints: IAccessPointEntity[] = [];
      let combinedArray: any = [];
      combinedArray = combinedArray.concat(
        this.accessPointEntityOrignal,
        userGroupDetails.accessPoints
      );
      combinedArray = new Set(combinedArray);
      combinedArray.forEach((accessPointId: string) => {
        if (
          userGroupDetails.accessPoints?.includes(accessPointId) &&
          !this.accessPointEntityOrignal?.includes(accessPointId)
        )
          addAccessPoints.push({ accessPointId });
        else if (
          this.accessPointEntityOrignal?.includes(accessPointId) &&
          !userGroupDetails.accessPoints?.includes(accessPointId)
        )
          deleteAccessPoints.push({ accessPointId });
      });
      let updateUserGroupPayload: IUserGroupUpdateDetails = {
        siteId: userGroupDetails.siteId,
        userGroupId: userGroupDetails?.userGroupId,
        userGroupName: userGroupDetails?.userGroupName,
        userGroupDescription: userGroupDetails?.userGroupDescription,
        selectedUsers: userGroupDetails?.selectedUsers,
        addAccessPoints: addAccessPoints,
        deleteAccessPoints: deleteAccessPoints,
      };
      this.userManagementService
        .updateUserGroup(updateUserGroupPayload)
        .subscribe({
          next: (response: any) => {
            if (this.selectedUsers?.length > 0) {
              this.addUsersToNewUserGroup(
                this.selectedUsers,
                response?.userGroupId
              );
            }
            this.notificationService.showSuccess(
              this.translate.instant('SUCCESSFULLY_UPDATED')
            );
            for (let i = 0; i < this.userGroupList?.length; i++) {
              if (this.userGroupList[i].userGroupId === response.userGroupId) {
                this.userGroupList[i].accessPoints =
                  userGroupDetails?.accessPoints;
                this.userGroupList[i].userGroupName =
                  userGroupDetails?.userGroupName;
                this.userGroupList[i].userGroupDescription =
                  userGroupDetails?.userGroupDescription;
              }
            }
            this.sharedDataService.markAll = false;
            this.stepper = 1;
          },
          error: (err: Error) => {
            if (window.__dev__) console.log('submitToParent', err);
            this.notificationService.showError(err.message);
          },
        });
    } else {
      if (this.formGroup.value?.accessTime?.repeat) {
        const userStartTimeEndTime = this.startTimeEndTimeUpdate();
        userGroupDetails.repeatStartTime = userStartTimeEndTime.repeatStartTime;
        userGroupDetails.repeatEndTime = userStartTimeEndTime.repeatEndTime;
        userGroupDetails.repeatDays =
          this.formGroup.value?.accessTime?.repeatDays;
      } else {
        this.formGroup.value?.accessTime?.startTime
          ? (userGroupDetails.startTime = convertParseDateTime(
              this.formGroup.value?.accessTime?.startTime,
              "EEE, MMM do, yyyy 'at' h:mm a",
              "yyyy-MM-dd'T'HH:mm:ss"
            ))
          : (userGroupDetails.startTime = CONSTANTS.NOW);

        this.formGroup.value?.accessTime?.expireTime
          ? (userGroupDetails.expireTime = convertParseDateTime(
              this.formGroup.value?.accessTime?.expireTime,
              "EEE, MMM do, yyyy 'at' h:mm a",
              "yyyy-MM-dd'T'HH:mm:ss"
            ))
          : null;
      }
      this.userManagementService.addUserGroup(userGroupDetails).subscribe({
        next: (addGroupResponse: any) => {
          if (this.selectedUsers?.length > 0) {
            this.addUsersToNewUserGroup(
              this.selectedUsers,
              addGroupResponse?.userGroupId,
              AccessLevelRequest['Resident_Admin'],
              true
            );
            //SUBMIT USER GROUP DETIALS TO PARENT userGroupDetails
            this.submitUserGroupToParent.emit(userGroupDetails);
          }

          this.stepper = 1;
          this.sharedDataService.markAll = false;
          userGroupDetails.userGroupId = addGroupResponse?.userGroupId;
          this.userGroupList?.push(userGroupDetails);
          this.resetSelectionState();
          this.notificationService.showSuccess(
            this.commonService.stringInterPolation(
              this.translate.instant('SUCCESSFULLY_CREATED'),
              this.translate.instant('GROUP')
            )
          );
        },
        error: (err: Error) => {
          if (window.__dev__) console.log('submitToParent', err);
          this.notificationService.showError(err.message);
        },
      });
    }
  }

  userStepperUpdate($event: number) {
    this.selectedGroupChange.emit(
      (this.selectedGroup = this.formGroup?.controls?.deviceGroup?.value?.group)
    );
    this.userStepperChange.emit($event);
  }

  userGroupUpdate($event: any) {
    this.userGroupChange.emit($event);
  }

  /**
   * @description This method removes user group
   * @param $event Contains ID to remove group
   */
  removeUserGroup($event: any) {
    const dialogInstance = this.helperService.openModalDialog(
      ConfirmRemoveDialogComponent,
      {
        title: this.translate.instant('REMOVE_THIS_GROUP'),
        bodyText: this.translate.instant(
          'ARE_YOU_SURE_YOU_WANT_TO_REMOVE_GROUP'
        ),
        footerText: this.translate.instant(
          'BY_MOVING_FORWARD_ALL_USER_ACCESS_FOR_GROUP'
        ),
      },
      CONSTANTS.LONG_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((result: boolean) => {
      if (result) {
        const groupID = this.formGroup.controls.addUserGroup.value.id;
        this.userManagementService.deleteGroup(groupID).subscribe({
          next: (res: any) => {
            this.userGroupList = this.userGroupList.filter(
              (group: IUserGroupDetails) =>
                group.userGroupId !== res?.userGroupId
            );
            this.notificationService.showSuccess(
              this.commonService.stringInterPolation(
                this.translate.instant('SUCCESSFULLY_DELETED'),
                this.translate.instant('GROUP')
              )
            );
            this.stepper--;
          },
          error: (err: Error) => {
            this.notificationService.showError(err?.message);
          },
        });
      }
    });
  }

  /**
   * @description close modal popup
   * @returns void
   */
  closeDialog() {
    if (this.orignalFormData === JSON.stringify(this.formGroup.value)) {
      this.dialogRef?.close();
      return;
    }

    const dialogInstance = this.helperService.openModalDialog(
      ConfirmDialogComponent,
      {
        title: this.translate.instant('TITLE'),
        headingText: this.translate.instant('HEADING_TEXT'),
        bodyText: this.translate.instant('BODY_TEXT'),
        footerText: this.translate.instant('FOOTER_TEXT'),
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.dialogRef?.close();
      }
    });
    this.buildingList?.forEach((building: any) => {
      building.buildingCount = 0;
    });
  }

  /**
   * @param buildingId selected building id
   * @description get floors and units given buildingid
   * @returns void
   */
  getUsersBySiteId(siteId: string): void {
    this.userManagementService
      .getAllUsers(
        siteId,
        CONSTANTS.GET_USERS_BY_SITES_OPERATION_VIEW,
        1000,
        undefined
      )
      .subscribe({
        next: (res: any) => {
          this.users = res.map((user: any) => {
            user.status = false;
            return user;
          });

          this.users = this.users.filter((outerUser: any) => {
            return !this.existingUsers?.some(
              (innerUser: any) => innerUser?.id === outerUser?.userId
            );
          });

          this.selectionListComponent?.updateUserCopy(this.users);
        },
        error: (err: Error) => {
          if (window.__dev__) console.log('MultiUser Selection List ', err);
        },
      });
  }

  /**
   * @description This empty method is for preventing stepper change on form submission.
   */
  disposeStepperChange() {
    //do nothing on stepper change.
  }

  /**
   *
   * @description This method is used  for getting users out from multi-user-selection-list component.
   */
  submitSelectedUsers(selectedUsers: any): void {
    this.selectedUsers = selectedUsers;
    this.submitToParent();
  }

  /**
   * @description This is back button for multi-user-selection-list.
   */
  childBackButtonHandler() {
    this.stepper = this.stepper - 1;
  }

  /**
   * @description this method is for incrementing step on continue access point screen
   */
  incrementStep() {
    this.stepper = this.stepper + 1;
  }

  /**
   * @description this method is for submitting the user comming from user profile flow
   */
  submitProfileUser() {
    let user = [
      {
        userId: this.profileDetails?.email,
        accessLevel: AccessLevelRequest['Resident_Admin'],
      },
    ];

    this.selectedUsers = user;
    this.submitToParent();
  }

  /**
   * @description this method is used to reset all the selection for users and access point.
   */
  resetSelectionState() {
    //reseting user selected list
    this.selectedUsers = undefined;
    //resetting building list count
    this.buildingList?.forEach((building: any, index: any) => {
      if (this.buildingList) this.buildingList[index].buildingCount = 0;
    });
    this.resetUserStatus();
    this.selectionListComponent?.updateUserCopy(this.users);
  }

  /**
   * Resets the 'status' property of users to 'false'.
   * Maps over the users array and updates the 'status' property of each user to 'false'.
   * This function modifies the users array in place.
   */
  private resetUserStatus(): void {
    this.users = this.users?.map((user: any) => {
      user.status = false;
      return user;
    });
  }

  addUsersToNewUserGroup(
    selectedUsers: any,
    groupId: string,
    defaultUserType: number = AccessLevelRequest['Resident_Admin'],
    loaderState: boolean = false
  ): void {
    const userIds = selectedUsers?.map((user: any) => {
      return {
        id: user?.userId,
        accessLevel: defaultUserType,
      };
    });
    let userGroupDetails: IUserGroupDetails = {};
    if (this.formGroup.value?.accessTime?.repeat) {
      const userStartTimeEndTime = this.startTimeEndTimeUpdate();
      userGroupDetails.repeatStartTime = userStartTimeEndTime.repeatStartTime;
      userGroupDetails.repeatEndTime = userStartTimeEndTime.repeatEndTime;
      userGroupDetails.repeatDays =
        this.formGroup.value?.accessTime?.repeatDays;
    } else {
      this.formGroup.value?.accessTime?.startTime
        ? (userGroupDetails.startTime = convertParseDateTime(
            this.formGroup.value?.accessTime?.startTime,
            "EEE, MMM do, yyyy 'at' h:mm a",
            "yyyy-MM-dd'T'HH:mm:ss"
          ))
        : (userGroupDetails.startTime = CONSTANTS.NOW);

      this.formGroup.value?.accessTime?.expireTime
        ? (userGroupDetails.expireTime = convertParseDateTime(
            this.formGroup.value?.accessTime?.expireTime,
            "EEE, MMM do, yyyy 'at' h:mm a",
            "yyyy-MM-dd'T'HH:mm:ss"
          ))
        : null;
    }
    if (this.formGroup.value?.accessTime?.repeat) {
      this.variables = {
        repeatStartTime: userGroupDetails.repeatStartTime,
        repeatEndTime: userGroupDetails.repeatEndTime,
        repeatDays: userGroupDetails.repeatDays,
        siteId: this.sharedData?.selectedSite?.siteId?.toString(),
        userGroupId: groupId,
        users: userIds,
      };
    } else {
      this.variables = {
        startTime: userGroupDetails.startTime,
        expireTime: userGroupDetails.expireTime,
        siteId: this.sharedData?.selectedSite?.siteId?.toString(),
        userGroupId: groupId,
        users: userIds,
      };
    }

    this.userManagementService
      ?.addUsersToUserGroup(this.variables, loaderState)
      .subscribe({
        next: (res: any) => {
          this.resetUserStatus();
          this.notificationService?.showSuccess(
            this.commonService?.stringInterPolation(
              this.translate.instant('SUCCESSFULLY_ADDED'),
              this.translate.instant('GROUP_USERS')
            )
          );
          this.submitUserGroupToParent.emit(userGroupDetails);
        },
        error: (err: Error) => {
          this.resetUserStatus();
          this.notificationService?.showError(err?.message);
        },
      });
  }

  /**
   * @description form submit get all fromgroup reference here to transform data
   * as per server object requirement
   * @returns void
   */
  startTimeEndTimeUpdate(): IUserGroupDetails {
    const userGroupDetails: IUserGroupDetails = {};

    if (
      !(
        this.formGroup.value?.accessTime?.repeatStartTime &&
        this.formGroup.value?.accessTime?.repeatEndTime
      )
    ) {
      // default value MPC-6375
      userGroupDetails.repeatStartTime = convertParseDateTime(
        '12:00 AM',
        'hh:mm a',
        'HH:mm'
      );
      userGroupDetails.repeatEndTime = convertParseDateTime(
        '11:59 PM',
        'hh:mm a',
        'HH:mm'
      );
    } else {
      userGroupDetails.repeatStartTime = convertParseDateTime(
        this.formGroup.value?.accessTime?.repeatStartTime,
        'hh:mm a',
        'HH:mm'
      );

      userGroupDetails.repeatEndTime = convertParseDateTime(
        this.formGroup.value?.accessTime?.repeatEndTime,
        'hh:mm a',
        'HH:mm'
      );
    }
    return userGroupDetails;
  }

  ngOnDestroy() {
    this.sharedDataService.markAll = false;
    this.subscription?.unsubscribe();
  }
}
